// frontend/src/components/AdminDashboard.js
//
// Global-admin "My Dashboard". A global_admin is a superuser: it can see every
// other role's dashboard. Rather than three nav entries, this page carries a
// "View as" bar that swaps the lens in place:
//
//   Finance  → the finance-admin company-wide FinanceDashboard
//   Manager  → the manager team-commission ManagerDashboard
//   AE       → any individual rep's RepDashboard (pick from the AE list)
//
// The AE lens passes repId to RepDashboard, which the backend honors ONLY for a
// global_admin (see /api/reps resolveRepId). No calc logic lives here — this is
// purely a shell that renders one of the existing dashboards.
import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { getReps } from '../api/client';
import FinanceDashboard from './FinanceDashboard';
import ManagerDashboard from './ManagerDashboard';
import RepDashboard from './RepDashboard';

const MODES = [
  { key: 'finance', label: 'Finance' },
  { key: 'manager', label: 'Manager' },
  { key: 'ae',      label: 'Individual AE' },
];

// initialMode: 'finance' | 'manager' | 'ae' — the lens to open on. The Sales
// "AE Dashboards" page (for global admins viewing Sales from Operations / Finance)
// opens straight on the AE picker.
export default function AdminDashboard({ initialMode = 'finance' } = {}) {
  const [mode, setMode]   = useState(initialMode);
  const [repId, setRepId] = useState('');

  const { data: repsData, loading: repsLoading } = useApi(getReps, []);
  const reps = repsData?.reps || [];

  return (
    <div>
      {/* ── "View as" bar ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
        padding: '16px 32px', borderBottom: '1px solid var(--border)',
        background: 'var(--bg-surface)', position: 'sticky', top: 0, zIndex: 10,
      }}>
        <span className="label" style={{ marginRight: '4px' }}>View as</span>

        {/* Mode segmented control */}
        <div style={{ display: 'flex', gap: '6px' }}>
          {MODES.map(m => {
            const active = mode === m.key;
            return (
              <button
                key={m.key}
                onClick={() => setMode(m.key)}
                className="btn btn-sm"
                style={{
                  background: active ? 'var(--blue-subtle)' : 'transparent',
                  border: `1px solid ${active ? 'var(--border-bright)' : 'var(--border)'}`,
                  color: active ? 'var(--blue-primary)' : 'var(--text-secondary)',
                  fontWeight: active ? 600 : 400,
                }}
              >
                {m.label}
              </button>
            );
          })}
        </div>

        {/* AE picker — only in AE mode */}
        {mode === 'ae' && (
          <select
            value={repId}
            onChange={e => setRepId(e.target.value)}
            className="input"
            style={{ width: 'auto', minWidth: '220px' }}
            disabled={repsLoading}
          >
            <option value="">{repsLoading ? 'Loading AEs…' : 'Select an AE…'}</option>
            {reps.map(r => (
              <option key={r.repId} value={r.repId}>
                {r.repName || `Rep ${r.repId}`}
              </option>
            ))}
          </select>
        )}

        <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-muted)' }}>
          Global Admin
        </span>
      </div>

      {/* ── Selected dashboard ── */}
      {mode === 'finance' && <FinanceDashboard />}
      {mode === 'manager' && <ManagerDashboard />}
      {mode === 'ae' && (
        repId
          ? <RepDashboard key={repId} repId={repId} />
          : <EmptyPrompt message="Select an AE above to view their dashboard." />
      )}
    </div>
  );
}

function EmptyPrompt({ message }) {
  return (
    <div style={{ padding: '64px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '14px' }}>
      {message}
    </div>
  );
}
